import React, { useState } from 'react';
import LearnBookmarkItem from './LearnBookmarkItem';

/**
 * LearnFolderItem - Display a folder with its bookmarks.
 *
 * Features:
 * - Expand/collapse
 * - Inline rename
 * - Delete with confirmation
 * - Contains LearnBookmarkItem children
 */
export default function LearnFolderItem({
  folder,
  isExpanded,
  onToggle,
  onRename,
  onDelete,
  onBookmarkClick,
  onMoveBookmark,
  onDeleteBookmark,
  allFolders
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(folder.name);

  const handleStartEdit = (e) => {
    e.stopPropagation();
    setEditName(folder.name);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== folder.name) {
      onRename(trimmed);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditName(folder.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <div className={`learn-folder-item ${isExpanded ? 'learn-folder-item--expanded' : ''}`}>
      {/* Folder header */}
      <div className="learn-folder-item__header" onClick={onToggle}>
        <span className="learn-folder-item__chevron">
          {isExpanded ? '▼' : '▶'}
        </span>

        {isEditing ? (
          <input
            type="text"
            className="learn-folder-item__edit-input"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSaveEdit}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <span className="learn-folder-item__name">
            {folder.name}
          </span>
        )}

        <span className="learn-folder-item__count">
          ({folder.bookmarks?.length || 0})
        </span>

        <div className="learn-folder-item__actions">
          <button
            className="learn-folder-item__action-btn"
            onClick={handleStartEdit}
            title="Rename folder"
          >
            ✎
          </button>
          <button
            className="learn-folder-item__action-btn learn-folder-item__action-btn--delete"
            onClick={handleDelete}
            title="Delete folder"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Folder contents */}
      {isExpanded && (
        <div className="learn-folder-item__content">
          {folder.bookmarks?.length > 0 ? (
            folder.bookmarks.map(bookmark => (
              <LearnBookmarkItem
                key={bookmark.id}
                bookmark={bookmark}
                onClick={() => onBookmarkClick?.(bookmark)}
                onMove={(folderId) => onMoveBookmark(bookmark.id, folderId)}
                onDelete={() => onDeleteBookmark(bookmark.id)}
                allFolders={allFolders}
                currentFolderId={folder.id}
              />
            ))
          ) : (
            <div className="learn-folder-item__empty">
              No bookmarks in this folder
            </div>
          )}
        </div>
      )}
    </div>
  );
}
